/**
 * SKUs management page.
 *
 * - Search-filtered list of every catalog entry (by code).
 * - Create row at top: code, Enter to commit.
 * - Delete with one-click confirm (button label flips to "Confirmar").
 * - Import button accepts .csv or .xlsx.
 *
 * The product name field was removed (Feedback V2 rev02 r2-13): it isn't used
 * for marcação/validação here and every row showed "(sem nome)" anyway, so the
 * screen now manages only the SKU code.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createSku,
  deleteSku,
  getSkusPage,
  importSkus,
  type Sku,
} from "@/api/skus";
import { ApiError } from "@/api/client";
import { canonicalSkuCode } from "@/components/sku";
import { colors } from "@/theme/colors";
import { skuColor } from "@/theme/skuColor";

const PAGE_SIZE = 50;

export function Skus() {
  const qc = useQueryClient();

  // Search input — debounced into `appliedQ` so every keystroke doesn't
  // refetch.  `appliedQ` participates in the query key so changing it
  // resets the infinite-query cursor pages.
  const [search, setSearch] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setAppliedQ(search.trim()), 200);
    return () => window.clearTimeout(t);
  }, [search]);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["skus-page", appliedQ],
    queryFn: ({ pageParam }) =>
      getSkusPage({
        q: appliedQ || undefined,
        cursor: pageParam,
        limit: PAGE_SIZE,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const rows = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  );

  // Banner for import / mutation feedback.
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  function show(success: string | null, failure: string | null) {
    setMsg(success);
    setErr(failure);
  }
  function asDetail(e: unknown): string {
    return e instanceof ApiError
      ? (e.body as { detail?: string })?.detail ?? e.message
      : String(e);
  }

  // ---------- import ----------
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importMut = useMutation({
    mutationFn: (file: File) => importSkus(file),
    onSuccess: (res, file) => {
      show(
        `${file.name}: ${res.total} linhas — ${res.created} novos, ` +
          `${res.updated} atualizados, ${res.unchanged} inalterados.`,
        null,
      );
      qc.invalidateQueries({ queryKey: ["skus"] });
      qc.invalidateQueries({ queryKey: ["skus-page"] });
    },
    onError: (e) => show(null, `Falha ao importar: ${asDetail(e)}`),
  });

  // ---------- create ----------
  const [newCode, setNewCode] = useState("");
  const createMut = useMutation({
    mutationFn: (code: string) => createSku(code),
    onSuccess: (sku) => {
      show(`SKU ${sku.code} criado.`, null);
      setNewCode("");
      qc.invalidateQueries({ queryKey: ["skus"] });
      qc.invalidateQueries({ queryKey: ["skus-page"] });
    },
    onError: (e) => show(null, `Falha ao criar: ${asDetail(e)}`),
  });

  // ---------- delete ----------
  const deleteMut = useMutation({
    mutationFn: (code: string) => deleteSku(code),
    onSuccess: (_, code) => {
      show(`SKU ${code} removido.`, null);
      qc.invalidateQueries({ queryKey: ["skus"] });
      qc.invalidateQueries({ queryKey: ["skus-page"] });
    },
    onError: (e) => show(null, `Falha ao remover: ${asDetail(e)}`),
  });

  // Canonical preview + best-effort duplicate hint against loaded rows
  // (rev03 r3-4). The backend is authoritative; this just gives instant
  // feedback before submitting.
  const canonicalNew = canonicalSkuCode(newCode);
  const normalizedChanged =
    canonicalNew.length > 0 && canonicalNew !== newCode.trim();
  const duplicateLoaded =
    canonicalNew.length > 0 && rows.some((s) => s.code === canonicalNew);

  function handleCreate() {
    const code = canonicalSkuCode(newCode);
    if (!code) {
      show(null, "Informe um código válido.");
      return;
    }
    createMut.mutate(code);
  }

  return (
    <div style={{ maxWidth: 960 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: "var(--space-base)",
        }}
      >
        <h2 style={{ fontSize: "var(--fs-display)", lineHeight: "var(--lh-display)" }}>
          SKUs
        </h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={importMut.isPending}
            style={secondaryButton}
          >
            {importMut.isPending ? "Importando…" : "⬆ Importar (.csv / .xlsx)"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) importMut.mutate(f);
            }}
          />
        </div>
      </div>

      {(msg || err) && (
        <div
          role="status"
          style={{
            marginBottom: "var(--space-base)",
            padding: "8px 12px",
            borderRadius: "var(--radius-sm)",
            background: err ? "rgba(220,38,38,0.08)" : "rgba(47,107,61,0.08)",
            color: err ? colors.danger : colors.primary,
            fontSize: "var(--fs-ui)",
          }}
        >
          {err ?? msg}
        </div>
      )}

      {/* search */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar por código…"
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "8px 10px",
          marginBottom: "var(--space-base)",
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-body)",
          border: `1px solid ${colors.line}`,
          borderRadius: "var(--radius-sm)",
          background: colors.panel,
          color: colors.text,
        }}
      />

      {/* create row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "20px 1fr auto",
          gap: 8,
          alignItems: "center",
          padding: "8px 6px",
          marginBottom: duplicateLoaded || normalizedChanged ? 4 : "var(--space-base)",
          background: colors.panel,
          border: `1px solid ${duplicateLoaded ? colors.danger : colors.line}`,
          borderRadius: "var(--radius-sm)",
        }}
      >
        <span aria-hidden style={{ color: colors.textMuted, textAlign: "center" }}>
          +
        </span>
        <input
          value={newCode}
          onChange={(e) => setNewCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !duplicateLoaded && handleCreate()}
          placeholder="código"
          style={inlineInput}
        />
        <button
          type="button"
          onClick={handleCreate}
          disabled={createMut.isPending || canonicalNew.length === 0 || duplicateLoaded}
          style={primaryButton}
        >
          ＋ Criar
        </button>
      </div>
      {(duplicateLoaded || normalizedChanged) && (
        <div
          style={{
            marginBottom: "var(--space-base)",
            fontSize: "var(--fs-ui)",
            color: duplicateLoaded ? colors.danger : colors.textMuted,
          }}
        >
          {duplicateLoaded
            ? `O código ${canonicalNew} já existe.`
            : `Será salvo como ${canonicalNew} (traços e espaços são ignorados).`}
        </div>
      )}

      {isLoading && <p style={{ color: colors.textMuted }}>Carregando…</p>}
      {error && (
        <p style={{ color: colors.danger }}>Falha ao carregar: {String(error)}</p>
      )}
      {!isLoading && rows.length === 0 && (
        <p style={{ color: colors.textMuted }}>
          {appliedQ
            ? "Nenhum resultado para a busca."
            : "Nenhum SKU ainda. Crie um acima ou importe."}
        </p>
      )}
      {rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div
            style={{
              fontSize: "var(--fs-ui)",
              color: colors.textMuted,
              padding: "4px 6px",
            }}
          >
            {rows.length} carregados{hasNextPage ? " (há mais)" : " (fim)"}
          </div>
          {rows.map((s) => (
            <SkuRow
              key={s.code}
              sku={s}
              isMutating={deleteMut.isPending}
              onDelete={() => deleteMut.mutate(s.code)}
            />
          ))}
          {hasNextPage && (
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              style={{
                ...secondaryButton,
                marginTop: "var(--space-tight)",
                alignSelf: "center",
              }}
            >
              {isFetchingNextPage ? "Carregando…" : "Carregar mais"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SkuRow({
  sku,
  isMutating,
  onDelete,
}: {
  sku: Sku;
  isMutating: boolean;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const tone = skuColor(sku.code);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "20px 1fr auto",
        gap: 8,
        alignItems: "center",
        padding: "6px 6px",
        background: colors.panel,
        border: `1px solid ${colors.line}`,
        borderRadius: "var(--radius-sm)",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 12,
          height: 12,
          borderRadius: 3,
          background: tone.fill,
          justifySelf: "center",
        }}
      />
      <span
        className="mono"
        style={{
          fontSize: "var(--fs-body)",
          color: colors.text,
        }}
      >
        {sku.code}
      </span>
      <button
        type="button"
        onClick={() => {
          if (confirming) {
            onDelete();
            setConfirming(false);
          } else {
            setConfirming(true);
            window.setTimeout(() => setConfirming(false), 3000);
          }
        }}
        disabled={isMutating}
        style={{
          ...secondaryButton,
          background: confirming ? colors.danger : colors.panel,
          color: confirming ? colors.onDanger : colors.text,
          borderColor: confirming ? colors.danger : colors.line,
        }}
      >
        {confirming ? "🗑 Confirmar?" : "🗑 Remover"}
      </button>
    </div>
  );
}

const inlineInput: React.CSSProperties = {
  padding: "6px 8px",
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-body)",
  border: `1px solid ${colors.line}`,
  borderRadius: "var(--radius-sm)",
  background: colors.canvas,
  color: colors.text,
};

const primaryButton: React.CSSProperties = {
  background: colors.primary,
  color: colors.onPrimary,
  padding: "8px 14px",
  borderRadius: "var(--radius-sm)",
  fontSize: "var(--fs-body)",
  fontWeight: 600,
  border: "none",
  cursor: "pointer",
};

const secondaryButton: React.CSSProperties = {
  background: colors.panel,
  color: colors.text,
  padding: "8px 14px",
  borderRadius: "var(--radius-sm)",
  fontSize: "var(--fs-body)",
  fontWeight: 500,
  border: `1px solid ${colors.line}`,
  cursor: "pointer",
};
